import { forwardRef, useId, type ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  alertTitleClassName,
} from "@/shared/components/ui";

export const LEGACY_MANAGED_POINTER_TITLE = "Managed in the legacy CLM application";
export const LEGACY_MANAGED_POINTER_BODY = "Make this change in the legacy CLM application.";

export type NoticeHeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export interface WriteNoticeProps {
  className?: string;
  /**
   * Title element: a heading level matching the surrounding outline (default
   * h5, the shared AlertTitle level sibling alerts use), or "none" for a
   * non-heading title where the notice sits outside the page outline (the
   * shell host renders before the page's h1).
   */
  headingLevel?: NoticeHeadingLevel | "none";
  /**
   * `status` (default) announces politely. `note` is for a notice embedded in
   * a component that already owns a single live region (the company-location
   * picker), so it never competes with that announcer.
   */
  role?: "status" | "note";
}

export interface LegacyManagedPointerProps extends WriteNoticeProps {
  /** When supplied, renders a Dismiss action (shell rejection host). */
  onDismiss?: () => void;
}

/**
 * The one "managed in the legacy application" pointer (T035/T036): the
 * shared informational `Alert` (DES-1 inline feedback, informational = blue).
 * No link: runtime config carries no legacy application URL, and none is
 * hardcoded. Programmatically focusable (`tabIndex=-1`) so a surface whose
 * focused control was withdrawn or disabled can hand focus here.
 */
export const LegacyManagedPointer = forwardRef<HTMLDivElement, LegacyManagedPointerProps>(
  function LegacyManagedPointer(
    { className, headingLevel = "h5", role = "status", onDismiss },
    ref,
  ) {
    return (
      <WriteNoticeFrame
        ref={ref}
        variant="info"
        role={role}
        headingLevel={headingLevel}
        className={className}
        title={LEGACY_MANAGED_POINTER_TITLE}
      >
        <p>{LEGACY_MANAGED_POINTER_BODY}</p>
        {onDismiss && (
          <Button type="button" variant="outline" className="mt-2" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </WriteNoticeFrame>
    );
  },
);

interface WriteNoticeFrameProps extends Required<Pick<WriteNoticeProps, "headingLevel" | "role">> {
  className?: string;
  variant: "info" | "warning";
  title: string;
  children: ReactNode;
}

/** Shared frame for the legacy pointer and the unavailable notice. */
export const WriteNoticeFrame = forwardRef<HTMLDivElement, WriteNoticeFrameProps>(
  function WriteNoticeFrame({ className, variant, role, headingLevel, title, children }, ref) {
    const titleId = useId();
    return (
      <Alert
        ref={ref}
        variant={variant}
        role={role}
        aria-labelledby={titleId}
        tabIndex={-1}
        className={className}
      >
        {headingLevel === "none" ? (
          <p id={titleId} className={alertTitleClassName}>
            {title}
          </p>
        ) : (
          <AlertTitle id={titleId} as={headingLevel}>
            {title}
          </AlertTitle>
        )}
        <AlertDescription>{children}</AlertDescription>
      </Alert>
    );
  },
);
